/**
 * EmailThreadDetail Component
 *
 * Displays email thread detail with all messages, attachments, and navigation.
 */

import { ArrowLeft, Mail, Send, Paperclip, User, Clock } from 'lucide-react';
import { Button, EmptyState, Skeleton, StatusPill, cn, formatDateigroesse, formatDatumZeit } from '@elbklar/ui';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface Attachment {
  filename: string;
  mime_type: string;
  size: number;
  gmail_attachment_id: string;
}

interface EmailMessage {
  id: string;
  thread_id: string;
  gmail_message_id: string | null;
  direction: 'inbound' | 'outbound';
  from_email: string;
  from_name: string | null;
  to_emails: string[];
  cc_emails: string[];
  subject: string;
  body_text: string | null;
  body_html: string | null;
  status: string;
  sent_at: string | null;
  received_at: string | null;
  has_attachments: boolean;
  attachments: Attachment[];
  created_at: string;
}

interface EmailThreadDetailData {
  id: string;
  subject: string;
  gmail_thread_id: string | null;
  customer_id: string | null;
  contact_id: string | null;
  customer_name: string | null;
  contact_name: string | null;
  messages: EmailMessage[];
}

interface EmailThreadDetailProps {
  thread: EmailThreadDetailData | null;
  isLoading?: boolean;
  onBack: () => void;
}

function MessageSkeleton() {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <Skeleton className="mb-2 h-5 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-6 w-16" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

function EmailMessageCard({ message }: { message: EmailMessage }) {
  const isInbound = message.direction === 'inbound';
  const timestamp = message.received_at || message.sent_at || message.created_at;

  return (
    <div className={cn('rounded-lg border border-border p-4', isInbound ? 'bg-card' : 'bg-muted')}>
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className={cn('flex size-8 items-center justify-center rounded-full', isInbound ? 'bg-muted text-muted-foreground' : 'bg-highlight-soft text-highlight')}>
              {isInbound ? <Mail size={15} strokeWidth={1.75} aria-hidden /> : <Send size={15} strokeWidth={1.75} aria-hidden />}
            </span>
            <div>
              <p className="font-medium">{message.from_name || message.from_email}</p>
              {message.from_name && <p className="t-small text-muted-foreground">{message.from_email}</p>}
            </div>
          </div>
          {message.to_emails.length > 0 && <p className="ml-10 mt-1 t-small text-muted-foreground">An: {message.to_emails.join(', ')}</p>}
          {message.cc_emails.length > 0 && <p className="ml-10 t-small text-muted-foreground">CC: {message.cc_emails.join(', ')}</p>}
        </div>
        <div className="flex items-center gap-2">
          <StatusPill tone={isInbound ? 'info' : 'highlight'}>{isInbound ? 'Eingang' : 'Ausgang'}</StatusPill>
          <span className="flex items-center gap-1 t-mono text-muted-foreground">
            <Clock size={12} strokeWidth={1.75} aria-hidden />
            {formatDatumZeit(timestamp)}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="ml-10 max-w-none">
        {message.body_html ? (
          <div dangerouslySetInnerHTML={{ __html: message.body_html }} className="t-body [&_a]:text-link" />
        ) : (
          <pre className="whitespace-pre-wrap font-sans t-body">{message.body_text || '(Kein Inhalt)'}</pre>
        )}
      </div>

      {/* Attachments */}
      {message.attachments && message.attachments.length > 0 && (
        <div className="ml-10 mt-4 border-t border-border pt-3">
          <p className="mb-2 flex items-center gap-1 t-body-strong">
            <Paperclip size={16} strokeWidth={1.75} aria-hidden />
            Anhänge ({message.attachments.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {/* Endpunkt seit 02.09.2026 (docs/it/plan-auth-luecke.md, Schritt 3):
                der Anhang kommt aus dem Postfach des Thread-Besitzers, das
                Sitzungs-Cookie geht beim Klick mit. */}
            {message.attachments.map((att, idx) => (
              <a
                key={idx}
                href={`${API_BASE_URL}/emails/attachments/${message.gmail_message_id}/${att.gmail_attachment_id}`}
                download={att.filename}
                className="flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1.5 text-[13px] transition-colors hover:bg-accent"
              >
                <Paperclip size={14} strokeWidth={1.75} className="text-muted-foreground" aria-hidden />
                <span className="max-w-32 truncate">{att.filename}</span>
                <span className="t-small text-muted-foreground">({formatDateigroesse(att.size)})</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ZurueckKnopf({ onBack }: { onBack: () => void }) {
  return (
    <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={onBack} className="mb-4">
      Zurück zur Liste
    </Button>
  );
}

export function EmailThreadDetail({
  thread,
  isLoading = false,
  onBack,
}: EmailThreadDetailProps) {
  if (isLoading) {
    return (
      <div>
        <ZurueckKnopf onBack={onBack} />
        <Skeleton className="mb-4 h-8 w-3/4" />
        <div className="space-y-4">
          <MessageSkeleton />
          <MessageSkeleton />
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div>
        <ZurueckKnopf onBack={onBack} />
        <EmptyState icon={Mail} title="E-Mail-Thread nicht gefunden" />
      </div>
    );
  }

  return (
    <div>
      <ZurueckKnopf onBack={onBack} />

      {/* Thread header */}
      <div className="mb-6">
        <h2 className="mb-2 t-page-title">{thread.subject || '(Kein Betreff)'}</h2>
        <div className="flex flex-wrap items-center gap-4 t-small text-muted-foreground">
          <span>
            {thread.messages.length} {thread.messages.length === 1 ? 'Nachricht' : 'Nachrichten'}
          </span>
          {thread.customer_name && (
            <span className="flex items-center gap-1">
              <User size={14} strokeWidth={1.75} aria-hidden />
              {thread.customer_name}
            </span>
          )}
          {thread.contact_name && <span>· {thread.contact_name}</span>}
        </div>
      </div>

      {/* Messages */}
      <div className="space-y-4">
        {thread.messages.map((message) => (
          <EmailMessageCard key={message.id} message={message} />
        ))}
      </div>
    </div>
  );
}

export default EmailThreadDetail;
